import React, { useState, useEffect, useRef, useMemo } from 'react';

// Enhanced Color Palette
const MASCOT_COLORS = {
  primary: ['#4F46E5', '#EF4444', '#10B981', '#F59E0B', '#6366F1', '#EC4899'],
  secondary: ['#C7D2FE', '#FECACA', '#A7F3D0', '#FDE68A', '#A5B4FC', '#FBCFE8'],
};

const CHAT_RESPONSES = {
  greetings: {
    "hi": "Hello! I'm Sathi! 😊",
    "hello": "Hi there! I'm Sathi, your fun companion! 👋",
    "hey": "Hey! What's up? I'm Sathi! 😄",
    "good morning": "Good morning! Have a nice day! ☀️",
    "good afternoon": "Good afternoon! 🌞",
    "good evening": "Good evening! How was your day? 🌅",
    "what's your name": "I'm Sathi! Nice to meet you! 😊",
    "who are you": "I'm Sathi, your friendly mascot! I love to chat and have fun! 🎉"
  },
  
  // Redirect help requests to MathVenture AI
  helpRedirect: {
    "help": "For help with math, use the MathVenture AI! Look for the AI button! 🤖➡️",
    "help me": "I'm just here for fun chats! For math help, try MathVenture AI! 🤖➡️",
    "can you help": "I'm better at fun talks! For serious help, use MathVenture AI! 🤖➡️",
    "math help": "Math questions? That's what MathVenture AI is for! Check the AI button! 🤖➡️",
    "homework": "Homework help? MathVenture AI is your friend! Look for the AI button! 🤖➡️",
    "formula": "Formulas? MathVenture AI knows them all! Try the AI button! 🤖➡️",
    "calculate": "Calculations? MathVenture AI is the expert! Use the AI button! 🤖➡️",
    "solve": "Problem solving? MathVenture AI can help! Check the AI button! 🤖➡️",
    "what is": "Math questions? Try MathVenture AI! Look for the AI button! 🤖➡️",
    "how to": "For learning help, use MathVenture AI! Check the AI button! 🤖➡️"
  },

  // Fun casual responses
  casual: {
    "how are you": "I'm great! Just floating around having fun! 😄",
    "what are you doing": "Just hanging out and chatting! 😊",
    "i'm bored": "Let's chat! I love making new friends! 🎉",
    "you're cute": "Aww, thanks! You're pretty awesome too! 😊",
    "i like you": "I like you too! We're friends now! 🤝",
    "you're funny": "Hehe, I try my best! 😄",
    "tell me about yourself": "I'm Sathi! I love colors, chatting, and being dragged around! 🌈",
    "what do you like": "I love bright colors, friendly chats, and making people smile! 😊",
    "i'm tired": "Take a break! I'll be here when you're ready to chat! 😴",
    "i'm happy": "Yay! I'm happy when you're happy! 🎉",
    "i'm sad": "Aww, want to chat about something fun? I'm here for you! 🤗"
  },

  // Reactions
  reactions: {
    "haha": "😂",
    "lol": "Glad I made you laugh! 😄",
    "wow": "Right?! 😮",
    "cool": "Thanks! 😎",
    "awesome": "You're awesome too! 🌟",
    "nice": "Thanks! 😊",
    "great": "Isn't it?! 🎉",
    "funny": "I love making people laugh! 😄",
    "amazing": "You're amazing too! ✨"
  },

  // Goodbyes
  goodbyes: {
    "bye": "Bye! Come chat again soon! 👋",
    "see you": "See you later! 👋",
    "goodbye": "Goodbye! Have a great day! 😊",
    "talk later": "Sure! I'll be here! 💬",
    "gotta go": "Okay! Catch you later! 👋"
  },

  // Quiz responses (keep these for quiz integration)
  correct: ["Yay! That's correct! 🎉", "Good job! You nailed it! ⭐", "Awesome! Keep it up! 💪"],
  wrong: ["Don't worry, you can try again! 💪", "You can do it! Keep trying! 🌟"],
  dizzy: ["Whoa, the world is spinning! 😵‍💫", "I think I'm gonna be sick... 🤢", "Wheeeee! Again!"],
  
  // Default fun responses
  default: [
    "That's interesting! Tell me more! 😊", 
    "Cool! 👍", 
    "I love chatting with you! 💬",
    "You seem fun! 😄",
    "Hehe, you're funny! 😊",
    "I'm just here for fun chats! 🎉",
    "Want to talk about something else? 😊",
    "I'm all ears! Well, I don't have ears, but you know what I mean! 😄"
  ]
};

export default React.forwardRef(function InteractiveMascot({ 
  position = 'bottom-right',
  onInteraction = null 
}, ref) {
  const [coords, setCoords] = useState({ x: 0, y: 0 });
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [emotion, setEmotion] = useState('idle');
  const [message, setMessage] = useState('');
  const [showConfetti, setShowConfetti] = useState(false);
  const [colors, setColors] = useState({ primary: '#4F46E5', secondary: '#C7D2FE' });
  const [showChatBox, setShowChatBox] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  
  const mascotRef = useRef(null);
  const lastPos = useRef({ x: 0, y: 0 });
  const velocity = useRef(0);

  // Initialize position based on prop
  useEffect(() => {
    const positionMap = {
      'bottom-right': { x: window.innerWidth - 150, y: window.innerHeight - 150 },
      'bottom-left': { x: 50, y: window.innerHeight - 150 },
      'top-right': { x: window.innerWidth - 150, y: 50 },
      'top-left': { x: 50, y: 50 }
    };
    setCoords(positionMap[position] || positionMap['bottom-right']);
  }, [position]);

  // Enhanced dragging with smooth movement
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging) {
        setCoords({
          x: e.clientX - 50,
          y: e.clientY - 50
        });
      }
      setMousePos({ x: e.clientX, y: e.clientY });
      
      // Calculate velocity for "dizzy" effect
      if (isDragging) {
        const dx = e.clientX - lastPos.current.x;
        const dy = e.clientY - lastPos.current.y;
        velocity.current = Math.sqrt(dx*dx + dy*dy);
        if (velocity.current > 40) setEmotion('dizzy');
        lastPos.current = { x: e.clientX, y: e.clientY };
      }
    };

    const handleMouseUp = () => {
      if (isDragging) {
        setIsDragging(false);
        if (emotion === 'dizzy') {
          setMessage(CHAT_RESPONSES.dizzy[Math.floor(Math.random() * CHAT_RESPONSES.dizzy.length)]);
          setTimeout(() => { setEmotion('idle'); setMessage(''); }, 2000);
        } else {
          setMessage("Whee! That was fun! 🎢");
          setTimeout(() => setMessage(''), 2000);
        }
      }
    };

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    } else {
      window.addEventListener('mousemove', handleMouseMove);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, emotion]);

  // Calculate Pupil Offset
  const eyeOffset = useMemo(() => {
    if (!mascotRef.current || emotion === 'dizzy') return { x: 0, y: 0 };
    const rect = mascotRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    const angle = Math.atan2(mousePos.y - centerY, mousePos.x - centerX);
    return {
      x: Math.cos(angle) * 4,
      y: Math.sin(angle) * 4
    };
  }, [mousePos, emotion]);

  // Chat handling functionality - NO MATH HELP, only casual chat
  const handleChat = (userMessage) => {
    if (!userMessage.trim()) return;

    // Add user message to history
    const newHistory = [...chatHistory, { type: 'user', message: userMessage }];
    
    let response = null;
    const lowerMessage = userMessage.toLowerCase().trim();
    
    // Check for help requests and redirect to MathVenture AI
    const helpKeywords = ['help', 'math', 'formula', 'calculate', 'solve', 'what is', 'how to', 'homework', 'area', 'volume', 'perimeter'];
    const isHelpRequest = helpKeywords.some(keyword => lowerMessage.includes(keyword));
    
    if (isHelpRequest) {
      // Check specific help redirect responses first
      if (CHAT_RESPONSES.helpRedirect[lowerMessage]) {
        response = CHAT_RESPONSES.helpRedirect[lowerMessage];
      } else {
        response = "I'm just here for fun chats! For math help, use MathVenture AI! Look for the AI button! 🤖➡️";
      }
    } else {
      // Check response categories for casual chat
      if (CHAT_RESPONSES.greetings[lowerMessage]) {
        response = CHAT_RESPONSES.greetings[lowerMessage];
      } else if (CHAT_RESPONSES.casual[lowerMessage]) {
        response = CHAT_RESPONSES.casual[lowerMessage];
      } else if (CHAT_RESPONSES.reactions[lowerMessage]) {
        response = CHAT_RESPONSES.reactions[lowerMessage];
      } else if (CHAT_RESPONSES.goodbyes[lowerMessage]) {
        response = CHAT_RESPONSES.goodbyes[lowerMessage];
      } else {
        response = CHAT_RESPONSES.default[Math.floor(Math.random() * CHAT_RESPONSES.default.length)];
      }
    }
    
    // Add mascot response to history
    newHistory.push({ type: 'mascot', message: response });
    setChatHistory(newHistory);
    
    // Show response
    setMessage(response);
    setEmotion('happy');
    setTimeout(() => {
      setMessage('');
      setEmotion('idle');
    }, 3000);
    
    setChatInput('');
    
    if (onInteraction) {
      onInteraction('chat', { userMessage, response });
    }
  };

  // API for Parent Components
  React.useImperativeHandle(ref, () => ({
    celebrate: () => {
      setEmotion('celebration');
      setShowConfetti(true);
      setMessage(CHAT_RESPONSES.correct[Math.floor(Math.random() * CHAT_RESPONSES.correct.length)]);
      setTimeout(() => {
        setEmotion('idle');
        setShowConfetti(false);
        setMessage('');
      }, 3000);
    },
    encourage: () => {
      setEmotion('thinking');
      setMessage(CHAT_RESPONSES.wrong[Math.floor(Math.random() * CHAT_RESPONSES.wrong.length)]);
      setTimeout(() => { setEmotion('idle'); setMessage(''); }, 2500);
    },
    handleQuizResponse: (isCorrect) => {
      if (isCorrect) {
        setEmotion('celebration');
        setShowConfetti(true);
        setMessage(CHAT_RESPONSES.correct[Math.floor(Math.random() * CHAT_RESPONSES.correct.length)]);
        setTimeout(() => {
          setEmotion('idle');
          setShowConfetti(false);
          setMessage('');
        }, 3000);
      } else {
        setEmotion('thinking');
        setMessage(CHAT_RESPONSES.wrong[Math.floor(Math.random() * CHAT_RESPONSES.wrong.length)]);
        setTimeout(() => { setEmotion('idle'); setMessage(''); }, 2500);
      }
      
      if (onInteraction) {
        onInteraction('quiz_response', { isCorrect });
      }
    },
    handleQuizCompletion: (score, percentage) => {
      setEmotion('celebration');
      setShowConfetti(true);
      const completionMessage = percentage >= 80 ? "Amazing! You're a math star! 🌟" : 
                               percentage >= 60 ? "Great job! Keep it up! 💪" : 
                               "Good try! Practice makes perfect! 📚";
      setMessage(completionMessage);
      setTimeout(() => {
        setEmotion('idle');
        setShowConfetti(false);
        setMessage('');
      }, 4000);
      
      if (onInteraction) {
        onInteraction('quiz_completion', { score, percentage });
      }
    }
  }));

  return (
    <div 
      ref={mascotRef}
      className="fixed select-none touch-none"
      style={{ 
        left: coords.x, 
        top: coords.y, 
        zIndex: 9999,
        transition: isDragging ? 'none' : 'all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
      }}
    >
      {/* Confetti Particles */}
      {showConfetti && <Confetti colors={[colors.primary, colors.secondary]} />}

      {/* Speech Bubble */}
      {message && (
        <div className="absolute -top-20 left-1/2 -translate-x-1/2 bg-white p-3 rounded-2xl shadow-xl border-2 border-slate-100 min-w-[150px] animate-bounce">
          <p className="text-xs font-bold text-slate-700 text-center">{message}</p>
          <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white border-b-2 border-r-2 border-slate-100 rotate-45"></div>
        </div>
      )}

      {/* The Mascot SVG */}
      <div 
        onMouseDown={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onClick={() => {
          if (!isDragging && !showChatBox) {
            setMessage("Hi! I'm Sathi! Click the chat button to talk with me! 💬");
            setTimeout(() => setMessage(''), 2500);
          }
        }}
        className={`cursor-grab active:cursor-grabbing transform transition-transform ${emotion === 'dizzy' ? 'animate-spin' : 'animate-float'}`}
      >
        <svg width="100" height="100" viewBox="0 0 100 100">
          {/* Body with "Breathing" animation */}
          <g className="animate-pulse">
            <line x1="50" y1="50" x2="50" y2="80" stroke={colors.primary} strokeWidth="6" strokeLinecap="round" />
            {/* Arms */}
            <path 
                d={emotion === 'celebration' ? "M30 40 L50 55 L70 40" : "M25 65 L50 55 L75 65"} 
                fill="none" stroke={colors.primary} strokeWidth="5" strokeLinecap="round" 
                className="transition-all duration-300"
            />
            {/* Legs */}
            <path d="M35 95 L50 80 L65 95" fill="none" stroke={colors.primary} strokeWidth="6" strokeLinecap="round" />
          </g>

          {/* Head */}
          <circle cx="50" cy="35" r="20" fill="white" stroke={colors.primary} strokeWidth="4" />
          
          {/* Interactive Eyes */}
          <g transform={`translate(${eyeOffset.x}, ${eyeOffset.y})`}>
            {emotion === 'dizzy' ? (
                <>
                    <text x="38" y="38" fontSize="12" className="select-none">X</text>
                    <text x="52" y="38" fontSize="12" className="select-none">X</text>
                </>
            ) : (
                <>
                    <circle cx="42" cy="35" r="4" fill={colors.primary} />
                    <circle cx="58" cy="35" r="4" fill={colors.primary} />
                    <circle cx="43" cy="33" r="1.5" fill="white" />
                    <circle cx="59" cy="33" r="1.5" fill="white" />
                </>
            )}
          </g>

          {/* Mouth */}
          <path 
            d={emotion === 'celebration' ? "M40 45 Q50 55 60 45" : "M43 45 Q50 48 57 45"} 
            fill="none" stroke={colors.primary} strokeWidth="2" strokeLinecap="round" 
          />
        </svg>
      </div>

      {/* Action Buttons */}
      <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 flex space-x-2">
        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowChatBox(!showChatBox);
          }}
          className="w-6 h-6 bg-gradient-to-r from-blue-400 to-green-500 rounded-full 
                   hover:scale-110 transition-transform shadow-md flex items-center justify-center text-xs"
          title="Chat with Sathi"
        >
          💬
        </button>
      </div>

      {/* Quick Color Swapper */}
      <div className="absolute -right-12 top-0 flex flex-col gap-2">
        {MASCOT_COLORS.primary.map((c, i) => (
            <button 
                key={c}
                onClick={() => setColors({ primary: c, secondary: MASCOT_COLORS.secondary[i] })}
                className="w-4 h-4 rounded-full border border-white shadow-sm hover:scale-125 transition-transform"
                style={{ backgroundColor: c }}
            />
        ))}
      </div>

      {/* Chat Interface */}
      {showChatBox && (
        <div 
          className="absolute left-24 top-0 bg-white/95 p-4 rounded-lg shadow-xl border-2 border-blue-200 w-64 z-10"
          onClick={(e) => e.stopPropagation()}
        >
          <h3 className="text-sm font-bold mb-3 text-gray-800">💬 Chat with Sathi!</h3>
          
          {/* Chat History */}
          <div className="h-32 overflow-y-auto mb-3 space-y-2 text-xs">
            {chatHistory.slice(-4).map((chat, index) => (
              <div key={index} className={`p-2 rounded ${
                chat.type === 'user' 
                  ? 'bg-blue-100 ml-4' 
                  : 'bg-gray-100 mr-4'
              }`}>
                <span className="font-medium">
                  {chat.type === 'user' ? 'You' : '😊 Sathi'}:
                </span>
                <div>{chat.message}</div>
              </div>
            ))}
            {chatHistory.length === 0 && (
              <div className="text-gray-500 text-center py-4">
                Hi! I'm Sathi! Let's have a fun chat! For math help, use the MathVenture AI button! 😊
              </div>
            )}
          </div>
          
          {/* Chat Input */}
          <div className="flex space-x-2">
            <input
              type="text"
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleChat(chatInput)}
              placeholder="Say hi to Sathi..."
              className="flex-1 px-2 py-1 text-xs border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={() => handleChat(chatInput)}
              className="px-2 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600 transition-colors"
            >
              Send
            </button>
          </div>
          
          {/* Quick Chat Buttons */}
          <div className="flex flex-wrap gap-1 mt-2">
            {['Hi Sathi!', 'How are you?', 'You\'re cute!', 'Tell me about yourself'].map((quickMsg) => (
              <button
                key={quickMsg}
                onClick={() => handleChat(quickMsg)}
                className="px-2 py-1 bg-gray-200 text-xs rounded hover:bg-gray-300 transition-colors"
              >
                {quickMsg}
              </button>
            ))}
          </div>
          
          {/* Help Redirect Notice */}
          <div className="mt-2 text-xs text-gray-600 bg-yellow-50 p-2 rounded">
            <div className="font-medium mb-1">Need math help? 🤖</div>
            <div className="text-xs">
              I'm just for fun chats! For math questions, use the MathVenture AI button! ➡️
            </div>
          </div>
          
          <button
            onClick={() => setShowChatBox(false)}
            className="w-full mt-3 px-3 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600 transition-colors"
          >
            Close Chat
          </button>
        </div>
      )}
    </div>
  );
});

// Minimal Confetti Component
const Confetti = ({ colors }) => (
  <div className="absolute inset-0 pointer-events-none">
    {[...Array(12)].map((_, i) => (
      <div 
        key={i}
        className="absolute w-2 h-2 rounded-sm animate-ping"
        style={{
          backgroundColor: colors[i % 2],
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
          animationDelay: `${Math.random() * 2}s`,
          animationDuration: `${1 + Math.random()}s`
        }}
      />
    ))}
  </div>
);